/**
 * Supplies the chat table renderer's dataset actions.
 *
 * This is the model-backed half of {@link ChatTableActions}: it lives in the
 * chat layer (mounted from the Coco page), so it can freely import the client,
 * artifact model, and API helpers. The consuming table renderer only touches the
 * context leaf, which keeps chat models out of the Streamdown render chain and
 * clear of the circular import that would otherwise crash it.
 */

import React, { useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { ResourceType } from '@cotera/api';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useChatArtifactModel } from './chat-artifact.model';
import { resourceArtifactData } from './artifacts/resource-artifact';
import {
  ChatTableActionsProvider,
  buildDatasetInput,
  type ChatTableActions,
} from '@cotera/client/v0/components/ai-elements/chat-table-actions';

const DEFAULT_DATASET_NAME = 'Untitled table';

const useChatTableActionsValue = (conversationId: string): ChatTableActions => {
  const client = useTenantedClient();
  const artifactModel = useChatArtifactModel();

  const createDataset = useCallback<ChatTableActions['createDataset']>(
    async (data, messageId) => {
      const id = uuid();
      const name = DEFAULT_DATASET_NAME;
      const { columns, rows } = buildDatasetInput(data);

      const result = await client.resource.dataset.createManual({
        id,
        name,
        columns,
        rows,
      });
      if (result.isErr()) {
        return null;
      }

      const artifactData = resourceArtifactData(ResourceType.Dataset, id);
      // When the source message is known, persist the dataset as a tool-call
      // result on that message (the artifact sigil) — it also records the
      // load_artifact pointer server-side. Otherwise fall back to just the
      // pointer.
      await client.resource.chat.addAction({
        conversationId,
        action:
          messageId !== null
            ? {
                type: 'add_tool_result_artifact',
                messageId,
                artifactType: ResourceType.Dataset,
                artifactId: id,
                data: artifactData,
              }
            : {
                type: 'add_artifact',
                artifactType: ResourceType.Dataset,
                artifactId: id,
                data: artifactData,
              },
      });
      artifactModel?.requestFocus(id);
      return { id, name };
    },
    [client, conversationId, artifactModel]
  );

  const openDataset = useCallback<ChatTableActions['openDataset']>(
    (id) => artifactModel?.requestFocus(id),
    [artifactModel]
  );

  return useMemo(
    () => ({ createDataset, openDataset }),
    [createDataset, openDataset]
  );
};

const ChatTableActionsScopeInner: React.FC<{
  conversationId: string;
  children: React.ReactNode;
}> = ({ conversationId, children }) => {
  const value = useChatTableActionsValue(conversationId);
  return (
    <ChatTableActionsProvider value={value}>
      {children}
    </ChatTableActionsProvider>
  );
};

/**
 * Provides {@link ChatTableActions} to the transcript. Mount inside the chat's
 * whoami + artifact-panel scope so its hooks resolve. Renders inert (no actions)
 * until a conversation id exists, which keeps the client hooks off the render
 * path until they can resolve.
 */
export const ChatTableActionsScope: React.FC<{
  conversationId: string | undefined;
  children: React.ReactNode;
}> = ({ conversationId, children }) => {
  if (conversationId === undefined) {
    return (
      <ChatTableActionsProvider value={null}>
        {children}
      </ChatTableActionsProvider>
    );
  }
  return (
    <ChatTableActionsScopeInner conversationId={conversationId}>
      {children}
    </ChatTableActionsScopeInner>
  );
};
